<template>
  <div v-if="client != null" :style="'padding-left:' + this.$store.state.sizes.ClientDetailNavigationWidth + 'px;'">

    <div class="p-6 border-b border-gray-300 bg-white w-full" 
      style="position:fixed;z-index:100;">
      
      <div class="text-xl font-thin text-gray-700 text-2xl">
        Uploading to Mercury
      </div>
    </div>

    <div style="padding:76px 0 50px 0;">
      <div class="p-6">
        <div class="">
          <template v-for="(message, index) in progressMessages">
            <div class="">
              {{ message }}
            </div>
          </template>
        </div>
      </div>
    </div>
  </div>
</template>

<script>

  import axios            from "axios"
  import AutoSaveLoader   from '@/shared/components/loaders/AutoSaveLoader'
  import ContentLoader    from '@/shared/components/loaders/ContentLoader'
  import { EventBus }     from '@/shared/event-bus';
  import _                from "lodash"

  export default {

    components: {
      AutoSaveLoader,
      ContentLoader,
    },

    data () {
      return {
        client          : null,
        exportData      : null,
        progressMessages: [],
        contentLoading  : false,
        autoSaving      : false,

        // keep the status of current applicant index
        currApplicantIndex : 0
      }
    },

    created() {
      this.getClient({
        clientId : this.$route.params.clientId,
        force    : true
      }, (client) => {
        this.client     = client
        this.exportData = client.export_data != null && client.export_data.length > 0 ? JSON.parse(client.export_data) : null

        // now that we have client, let's start the upload process
        this.upload()
      })
    },

    methods : {

      upload() {
        this.progressMessages.push("Preparing to upload applicants data")
        this.progressMessages.push(this.client.applicants.length + " applicants found")
        this.currApplicantIndex = 0
        // this.uploadApplicantStep1()
        this.uploadOpportunity()
      },

      // sometimes depending on how the process branches out, we need to do a different task becore 
      // coming back to the same path. Building up a large callback tree isn't ideal, but in our scenario
      // where we need to provide feedback, it actually makes sense. I'm sure there is a better way
      // to handle this using async, await.
      uploadApplicantStep1() {

        // make sure that currApplicantIndex < applicants.length
        if(currApplicantIndex < this.client.applicants.length) {

          this.progressMessages.push("Processing " + (this.currApplicantIndex + 1) + " of " + this.client.applicants.length + " applicants - " + this.getApplicantName(this.client.applicants[this.currApplicantIndex]))

          // first let's search the applicant in mercury contact
          this.progressMessages.push("Searching for applicant " + this.getApplicantName(this.client.applicants[this.currApplicantIndex]) + " in Mercury")
          this.searchApplicant({
            clientId   : this.client.id,
            applicantId: this.client.applicants[this.currApplicantIndex].id
          }, (mercuryContact) => {

            if(mercuryContact.statusCode == 200) {

              // is there a unique ID - then we update, otherwise we create
              if(mercuryContact.data != null && mercuryContact.data.length > 0) {
                this.progressMessages.push("Found applicant " + this.getApplicantName(this.client.applicants[this.currApplicantIndex]) + " in Mercury - preparing to update")

                // let's clean up some data before we update
                this.cleanProfileData({
                  clientId        : this.client.id,
                  applicantId     : this.client.applicants[this.currApplicantIndex].id,
                  mercuryContactId: mercuryContact.data[0].uniqueId,
                }, (results) => {
                  // console.log(results)
                  this.uploadApplicantStep2({
                    createOrUpdate  : 'update', 
                    mercuryContactId: mercuryContact.data[0].uniqueId
                  });
                })
              }

              else {
                this.progressMessages.push("Applicant " + this.getApplicantName(this.client.applicants[this.currApplicantIndex]) + " not found in Mercury")
                this.uploadApplicantStep2({
                  createOrUpdate: 'create', 
                });
              }
            }

          })
        }

        else {
          this.uploadOpportunity()
        }
      },

      // step 1 involves either creating or updating the given contact
      uploadApplicantStep2(params) {

        if(params.createOrUpdate == 'create') {
          this.progressMessages.push("Creating applicant " + this.getApplicantName(this.client.applicants[this.currApplicantIndex]) + " in Mercury")
        } else {
          this.progressMessages.push("Updating applicant " + this.getApplicantName(this.client.applicants[this.currApplicantIndex]) + " in Mercury")
        }

        // we can now either create or update the applicant
        this.createOrUpdateContact({
          clientId        : this.client.id,
          applicantId     : this.client.applicants[this.currApplicantIndex].id,
          mercuryContactId: params.createOrUpdate == 'update' ? params.mercuryContactId : null,
          createOrUpdate  : params.createOrUpdate
        }, (results) => {

          if(results.statusCode == 200) {

            // let's do another search for the contact to make sure we have the mercury contact ID
            // this also forces the DB to be updated with latest data in Mercury
            this.searchApplicant({
              clientId   : this.client.id,
              applicantId: this.client.applicants[this.currApplicantIndex].id
            }, (mercuryContact) => {

              if(mercuryContact.statusCode == 200 && mercuryContact.data != null && mercuryContact.data.length > 0) {

                // update the contact methods in mercury
                this.progressMessages.push("Updating contact information")  
                this.setContactMethods({
                  clientId        : this.client.id,
                  applicantId     : this.client.applicants[this.currApplicantIndex].id,
                  mercuryContactId: mercuryContact.data[0].uniqueId,
                }, (results) => {

                  if(results.statusCode == 200) {

                    this.progressMessages.push("Updating residence information")  
                    this.setAddresses({
                      clientId        : this.client.id,
                      applicantId     : this.client.applicants[this.currApplicantIndex].id,
                      mercuryContactId: mercuryContact.data[0].uniqueId,
                    }, (results) => {

                      if(results.statusCode == 200) {

                        this.progressMessages.push("Updating income information")  
                        this.setIncomes({
                          clientId        : this.client.id,
                          applicantId     : this.client.applicants[this.currApplicantIndex].id,
                          mercuryContactId: mercuryContact.data[0].uniqueId,
                        }, (results) => {

                          if(results.statusCode == 200) {

                            // at this point, if the client is primary then we will set the expenses
                            if(this.client.applicants[this.currApplicantIndex].person.isPrimary == 1) {
                              
                              this.progressMessages.push("Updating expense information")  
                              this.setExpenses({
                                clientId        : this.client.id,
                                applicantId     : this.client.applicants[this.currApplicantIndex].id,
                                mercuryContactId: mercuryContact.data[0].uniqueId,
                              }, (results) => {

                                if(results.statusCode == 200) {
                                  
                                  // // are there any dependants?
                                  // // if(this.client.applicants[index].person.)
                                  // this.progressMessages.push("Updating dependant information")  
                                  // this.setDependantAgesInMercury({
                                  //   clientId        : this.client.id,
                                  //   applicantId     : this.client.applicants[index].id,
                                  //   mercuryContactId: mercuryContact.uniqueId,
                                  // }, () => {

                                  // });
                                  
                                  // at this point, we increment the currApplicantIndex and go back to uploadApplicantStep1
                                  this.currApplicantIndex = this.currApplicantIndex + 1
                                  this.uploadApplicantStep1()
                                }

                              });

                            }

                            else {

                              // at this point, we increment the currApplicantIndex and go back to uploadApplicantStep1
                              this.currApplicantIndex = this.currApplicantIndex + 1
                              this.uploadApplicantStep1()
                            }
                          }

                        });

                      }

                    });

                  }

                });

              }
            })
          }

        })
      },



      uploadOpportunity() {

        if(this.exportData.newOpportunity) {
          this.progressMessages.push("Creating a new opportunity in Mercury")
        } else {
          this.progressMessages.push("Updating an opportunity in Mercury")
        }

        this.createOrUpdateOpportunity({
          clientId        : this.client.id,
          isNewOpportunity: this.exportData.newOpportunity ? '1' : '0'
        }, (opportunity) => {

          this.uploadDocuments(opportunity.data.uniqueId)

          // if(opportunity.statusCode == 200) {

          //   this.progressMessages.push("Updating asset data")
          //   this.setAssets({
          //     clientId     : this.client.id,
          //     opportunityId: opportunity.data.uniqueId
          //   }, (results) => {

          //     if(results.statusCode == 200) {
                
          //       this.progressMessages.push("Updating liability data")
          //       this.setLiabilities({
          //         clientId     : this.client.id,
          //         opportunityId: opportunity.data.uniqueId
          //       }, (results) => {

          //         if(results.statusCode == 200) {
                    
          //           this.progressMessages.push("Updating related parties")
          //           this.setRelatedParties({
          //             clientId     : this.client.id,
          //             opportunityId: opportunity.data.uniqueId
          //           }, (results) => {

          //             if(results.statusCode == 200) {

          //               this.progressMessages.push("Updating third parties")
          //               this.setThirdParties({
          //                 clientId     : this.client.id,
          //                 opportunityId: opportunity.data.uniqueId
          //               }, (results) => {

          //                 if(results.statusCode == 200) {
                        
          //                   this.progressMessages.push("Updating questionnaire")
          //                   this.setQuestionnaire({
          //                     clientId     : this.client.id,
          //                     opportunityId: opportunity.data.uniqueId
          //                   }, (results) => {

          //                     if(results.statusCode == 200) {
                                
          //                       // do we need to upload client summary?
          //                       if(this.exportData.clientSummary) {
          //                         this.progressMessages.push("Uploading client summary document")
          //                         this.uploadClientSummary({
          //                           clientId     : this.client.id,
          //                           opportunityId: opportunity.data.uniqueId
          //                         }, (results) => {

          //                           this.uploadDocuments(opportunity.data.uniqueId)
          //                         })
          //                       }

          //                       else {
          //                         this.uploadDocuments(opportunity.data.uniqueId)
          //                       }

          //                     }
                              
          //                   })
          //                 }
          //               })

          //             }

          //           })

          //         }

          //       })

          //     }

          //   })
          // }

        })
      },


      uploadDocuments(opportunityId) {
        this.progressMessages.push("Uploading documents for this client")
        this.uploadClientDocuments({
          clientId     : this.$route.params.clientId,
          opportunityId: opportunityId
        }, (results) => {

        })
      },


        

      searchApplicant(params, callback) {
        axios.get(process.env.VUE_APP_API_URL + '/api/mercury/contact/detail', {
          params,
          headers: {
            'Authorization' : 'Bearer ' + localStorage.getItem('bp-broker-jwt')
          }
        })
        .then(response => {
          callback({
            statusCode: response.status, 
            data      : response.data.results
          })
        })
        .catch(e => {
          callback({
            statusCode: e.response.status,
            error     : e.response.data.error
          })
        })
      },

      cleanProfileData(params, callback) {
        axios.get(process.env.VUE_APP_API_URL + '/api/mercury/contact/clean-profile-data', {
          params,
          headers: {
            'Authorization' : 'Bearer ' + localStorage.getItem('bp-broker-jwt')
          }
        })
        .then(response => {
          callback({
            statusCode: response.status,
            data      : response.data
          })
        })
        .catch(e => {
          callback({
            statusCode: e.response.status,
          })
        })
      },

      createOrUpdateContact(params, callback) {
        axios.get(process.env.VUE_APP_API_URL + '/api/mercury/contact/create-or-update', {
          params,
          headers: {
            'Authorization' : 'Bearer ' + localStorage.getItem('bp-broker-jwt')
          }
        })
        .then(response => {
          callback({
            statusCode: response.status,
            data      : response.data
          })
        })
        .catch(e => {
          callback({
            statusCode: e.response.status,
            data      : e.response.data
          })
        })
      },

      setContactMethods(params, callback) {
        axios.get(process.env.VUE_APP_API_URL + '/api/mercury/contact/set-contact-methods', {
          params,
          headers: {
            'Authorization' : 'Bearer ' + localStorage.getItem('bp-broker-jwt')
          }
        })
        .then(response => {
          callback({
            statusCode: response.status,
            data      : response.data
          })
        })
        .catch(e => {
          callback({
            statusCode: e.response.status,
            data      : e.response.data
          })
        })
      },

      setAddresses(params, callback) {
        axios.get(process.env.VUE_APP_API_URL + '/api/mercury/contact/set-addresses', {
          params,
          headers: {
            'Authorization' : 'Bearer ' + localStorage.getItem('bp-broker-jwt')
          }
        })
        .then(response => {
          callback({
            statusCode: response.status,
            data      : response.data
          })
        })
        .catch(e => {
          callback({
            statusCode: e.response.status,
            data      : e.response.data
          })
        })
      },

      setIncomes(params, callback) {
        axios.get(process.env.VUE_APP_API_URL + '/api/mercury/contact/set-incomes', {
          params,
          headers: {
            'Authorization' : 'Bearer ' + localStorage.getItem('bp-broker-jwt')
          }
        })
        .then(response => {
          callback({
            statusCode: response.status,
            data      : response.data
          })
        })
        .catch(e => {
          callback({
            statusCode: e.response.status,
            data      : e.response.data
          })
        })
      },

      setExpenses(params, callback) {
        axios.get(process.env.VUE_APP_API_URL + '/api/mercury/contact/set-expenses', {
          params,
          headers: {
            'Authorization' : 'Bearer ' + localStorage.getItem('bp-broker-jwt')
          }
        })
        .then(response => {
          callback({
            statusCode: response.status,
            data      : response.data
          })
        })
        .catch(e => {
          callback({
            statusCode: e.response.status,
            data      : e.response.data
          })
        })
      },

      setDependantAges(params, callback) {
        axios.get(process.env.VUE_APP_API_URL + '/api/mercury/contact/set-dependant-ages', {
          params,
          headers: {
            'Authorization' : 'Bearer ' + localStorage.getItem('bp-broker-jwt')
          }
        })
        .then(response => {
          callback()
        })
        .catch(e => {
          callback()
        })
      },






      createOrUpdateOpportunity(params, callback) {
        axios.get(process.env.VUE_APP_API_URL + '/api/mercury/opportunity/create-or-update', {
          params,
          headers: {
            'Authorization' : 'Bearer ' + localStorage.getItem('bp-broker-jwt')
          }
        })
        .then(response => {
          callback({
            statusCode: response.status,
            data      : response.data
          })
        })
        .catch(e => {
          callback({
            statusCode: e.response.status,
            data      : e.response.data
          })
        })
      },

      setAssets(params, callback) {
        axios.get(process.env.VUE_APP_API_URL + '/api/mercury/opportunity/set-assets', {
          params,
          headers: {
            'Authorization' : 'Bearer ' + localStorage.getItem('bp-broker-jwt')
          }
        })
        .then(response => {
          callback({
            statusCode: response.status,
            data      : response.data
          })
        })
        .catch(e => {
          callback({
            statusCode: e.response.status,
            data      : e.response.data
          })
        })
      },

      setLiabilities(params, callback) {
        axios.get(process.env.VUE_APP_API_URL + '/api/mercury/opportunity/set-liabilities', {
          params,
          headers: {
            'Authorization' : 'Bearer ' + localStorage.getItem('bp-broker-jwt')
          }
        })
        .then(response => {
          callback({
            statusCode: response.status,
            data      : response.data
          })
        })
        .catch(e => {
          callback({
            statusCode: e.response.status,
            data      : e.response.data
          })
        })
      },

      setRelatedParties(params, callback) {
        axios.get(process.env.VUE_APP_API_URL + '/api/mercury/opportunity/set-related-parties', {
          params,
          headers: {
            'Authorization' : 'Bearer ' + localStorage.getItem('bp-broker-jwt')
          }
        })
        .then(response => {
          callback({
            statusCode: response.status,
            data      : response.data
          })
        })
        .catch(e => {
          callback({
            statusCode: e.response.status,
            data      : e.response.data
          })
        })
      },

      setThirdParties(params, callback) {
        axios.get(process.env.VUE_APP_API_URL + '/api/mercury/opportunity/set-third-parties', {
          params,
          headers: {
            'Authorization' : 'Bearer ' + localStorage.getItem('bp-broker-jwt')
          }
        })
        .then(response => {
          callback({
            statusCode: response.status,
            data      : response.data
          })
        })
        .catch(e => {
          callback({
            statusCode: e.response.status,
            data      : e.response.data
          })
        })
      },

      setQuestionnaire(params, callback) {
        axios.get(process.env.VUE_APP_API_URL + '/api/mercury/questionnaire/create', {
          params,
          headers: {
            'Authorization' : 'Bearer ' + localStorage.getItem('bp-broker-jwt')
          }
        })
        .then(response => {
          callback({
            statusCode: response.status,
            data      : response.data
          })
        })
        .catch(e => {
          callback({
            statusCode: e.response.status,
            data      : e.response.data
          })
        })
      },

      uploadClientSummary(params, callback) {
        axios.get(process.env.VUE_APP_API_URL + '/api/mercury/document/upload-client-summary', {
          params,
          headers: {
            'Authorization' : 'Bearer ' + localStorage.getItem('bp-broker-jwt')
          }
        })
        .then(response => {
          callback({
            statusCode: response.status,
            data      : response.data
          })
        })
        .catch(e => {
          callback({
            statusCode: e.response.status,
            data      : e.response.data
          })
        })
      },

      uploadClientDocuments(params, callback) {
        axios.get(process.env.VUE_APP_API_URL + '/api/mercury/document/upload-documents', {
          params,
          headers: {
            'Authorization' : 'Bearer ' + localStorage.getItem('bp-broker-jwt')
          }
        })
        .then(response => {
          callback({
            statusCode: response.status,
            data      : response.data
          })
        })
        .catch(e => {
          callback({
            statusCode: e.response.status,
            data      : e.response.data
          })
        })
      },



    }

  }
</script>

<style scoped>

</style>